import { useEffect, useEffectEvent } from "react";
import equal from "fast-deep-equal";
import { useTranslation } from "react-i18next";
import {
  getEditedTrackData,
  isEditedTrackSaved,
} from "stores/routingStore/getTrackData";
import { useRouting } from "stores/routingStore/RoutingContext";
import { useUserTracks } from "stores/useUserTracks";
import { showErrorNotification } from "utils/notifications/customNotifications";

/**
 * Saves the own track open in the editor after each edit, once its elevations are recomputed. Used by MainContainer,
 * which is always mounted (the side panel with the tracks list isn't mounted on phones until it's first opened).
 *
 * Opening a track loads its saved data into the editor, so the editor's data is compared with the saved track:
 * that's the same whether the track was opened from the list, from the map, or just saved as a new track.
 * Elevations fetched for a track saved without them are new data, they're saved.
 */
export const useTrackAutosave = () => {
  const { selectedTrack, updateTrack } = useUserTracks();
  const {
    state: {
      routing,
      computedTrackpoints,
      nonRoutableTrackMetadata,
      trackWaypoints,
      elevations,
      trackMeta,
      trackColor,
    },
    openedTrack,
  } = useRouting();
  const { t } = useTranslation();

  // Reads the current track data whenever it's called, without making the effect below depend on it
  const autosave = useEffectEvent(() => {
    if (
      !selectedTrack ||
      trackWaypoints.length < 2 ||
      !computedTrackpoints ||
      !elevations
    ) {
      return;
    }
    const isSaved =
      isEditedTrackSaved(
        { routing, trackWaypoints, trackColor },
        selectedTrack
      ) && equal(elevations, selectedTrack.trackPointsEle ?? []);
    if (isSaved) return;

    updateTrack(
      selectedTrack.id,
      getEditedTrackData(
        {
          routing,
          trackWaypoints,
          computedTrackpoints,
          elevations,
          trackMeta,
          nonRoutableTrackMetadata,
          trackColor,
        },
        openedTrack
      )
    ).catch(() => {
      // Saved after every edit, the id keeps a lost connection from showing a pile of notifications
      showErrorNotification({
        id: "track-autosave-failed",
        title: t("generic.error_title"),
        message: t("error_modal.generic_text"),
      });
    });
  });

  useEffect(() => {
    autosave();
  }, [elevations]);
};
